import slug from 'slug';

import type { SupportedCurrency } from 'app/typings/core/common/enum/supportedCurrency';
import { SUPPORTED_CURRENCY } from 'app/typings/core/common/enum/supportedCurrency';

export function generateCode(codeLen: number): string {
  return Math.random()
    .toString(36)
    .substring(2, codeLen + 2)
    .toUpperCase();
}

export const pluralize = (
  n: number | null | undefined,
  single: string,
  plural: string,
  showCount = true
): string => {
  const count = n ?? 0;
  const word = count > 1 ? plural : single;

  return showCount ? `${count} ${word}` : word;
};

export const slugify = (str: string): string => slug(str, { lower: true });

export const upperCaseFirstLetter = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const toSnakeCase = (string?: string): string => {
  if (!string) {
    return '';
  }

  return (
    string.charAt(0).toLowerCase() +
    string.slice(1).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
  );
};

const INVISIBLE_CHARACTERS = new RegExp('[\\p{Cc}\\p{Cf}]', 'gu');

export const sanitizeRateCode = (value?: string): string =>
  (value ?? '').replace(INVISIBLE_CHARACTERS, '').trimStart();

export const DEFAULT_CURRENCY_LOCALE = 'en-US';

// Each currency is displayed with the convention of the country using it: 1 000 €, 1 000 CHF, but £1,000.
const CURRENCY_LOCALES: Record<SupportedCurrency, string> = {
  [SUPPORTED_CURRENCY.EUR]: 'fr-FR',
  [SUPPORTED_CURRENCY.GBP]: 'en-GB',
  [SUPPORTED_CURRENCY.CHF]: 'fr-CH',
};

export const getCurrencyLocale = (currency?: string): string =>
  CURRENCY_LOCALES[currency as SupportedCurrency] ?? DEFAULT_CURRENCY_LOCALE;

export const formatCurrency = (
  price: number,
  currency?: string,
  defaultText = '',
  locale: string = DEFAULT_CURRENCY_LOCALE
): string => {
  const intlConfig: Intl.NumberFormatOptions = {
    currency,
    style: currency ? 'currency' : undefined,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  };

  if (!currency) {
    return price.toFixed(2);
  }

  return typeof price === 'number'
    ? new Intl.NumberFormat(locale, intlConfig).format(price)
    : defaultText;
};
